/**
 * Galaxia Metaverse Bridge — API client
 * Uses REACT_APP_API_URL in production; same-origin /api via CRA proxy in development.
 */

const BASE = process.env.REACT_APP_API_URL || '/api';

function authHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('galaxia_jwt') : null;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const csrf = typeof document !== 'undefined'
    ? document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith('csrf-token='))
    : null;
  if (csrf) headers['X-CSRF-Token'] = decodeURIComponent(csrf.split('=')[1]);
  return headers;
}

async function apiFetch(path, options = {}) {
  const response = await fetch(`${BASE}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
    credentials: 'include'
  });
  return response.json();
}

export const bridgeAPI = {
  async lockAsset(data) {
    return apiFetch('/bridge/lock', { method: 'POST', body: JSON.stringify(data) });
  },
  async unlockAsset(data) {
    return apiFetch('/bridge/unlock', { method: 'POST', body: JSON.stringify(data) });
  },
  async mintWrappedAsset(data) {
    return apiFetch('/bridge/mint-wrapped', { method: 'POST', body: JSON.stringify(data) });
  },
  async burnWrappedAsset(data) {
    return apiFetch('/bridge/burn-wrapped', { method: 'POST', body: JSON.stringify(data) });
  },
  async getUserAssets(userAddress) {
    return apiFetch(`/bridge/asset-locks/${userAddress}`);
  },
  async getUserWrappedAssets(userAddress) {
    return apiFetch(`/bridge/wrapped-assets/${userAddress}`);
  },
  async getSupportedMetaverses() {
    return apiFetch('/bridge/metaverses/supported');
  },
  async calculateBridgeFee(metaverseId, assetValue) {
    return apiFetch('/bridge/calculate-fee', {
      method: 'POST',
      body: JSON.stringify({ metaverseId, assetValue })
    });
  },
  async getBridgeStatus() {
    return apiFetch('/bridge/status');
  },
  async validateTransaction(data) {
    return apiFetch('/bridge/validate-transaction', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
};

export const metadataAPI = {
  async translateAvatarMetadata(sourceMetaverse, metadata, options) {
    return apiFetch('/metadata/translate/avatar', {
      method: 'POST',
      body: JSON.stringify({ sourceMetaverse, sourceMetadata: metadata, options })
    });
  },
  async translateAssetMetadata(sourceMetaverse, metadata, options) {
    return apiFetch('/metadata/translate/asset', {
      method: 'POST',
      body: JSON.stringify({ sourceMetaverse, sourceMetadata: metadata, options })
    });
  },
  async getMetadataSchema(metaverse, type) {
    return apiFetch(`/metadata/schemas/${metaverse}/${type}`);
  },
  async getSupportedMetaverses() {
    return apiFetch('/metadata/metaverses');
  }
};

export const avatarAPI = {
  async registerAvatar(userAddress, metadata, metaverseId) {
    return apiFetch('/avatar/register', {
      method: 'POST',
      body: JSON.stringify({ userAddress, avatarMetadata: metadata, metaverseId })
    });
  },
  async getAvatar(avatarId) {
    return apiFetch(`/avatar/${avatarId}`);
  },
  async getUserAvatars(userAddress) {
    return apiFetch(`/avatar/user/${userAddress}`);
  },
  async updateAvatar(avatarId, updates) {
    return apiFetch(`/avatar/${avatarId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },
  async linkAvatarToMetaverse(avatarId, metaverseId, translatedMetadata) {
    return apiFetch(`/avatar/${avatarId}/link`, {
      method: 'POST',
      body: JSON.stringify({ metaverseId, translatedMetadata })
    });
  },
  async generatePortabilityPackage(avatarId, targetMetaverse, options = {}) {
    return apiFetch(`/avatar/${avatarId}/port`, {
      method: 'POST',
      body: JSON.stringify({ targetMetaverse, options })
    });
  },
  async getSupportedStandards() {
    return apiFetch('/avatar/standards/supported');
  }
};

export const postQuantumAPI = {
  async getHealth() {
    return apiFetch('/post-quantum/health');
  },
  async getAlgorithms() {
    return apiFetch('/post-quantum/algorithms');
  }
};

export const complianceAPI = {
  async getDashboard() {
    return apiFetch('/compliance/dashboard');
  }
};

export const ecosystemAPI = {
  async getLinks() {
    return apiFetch('/ecosystem');
  }
};

export const handleAPIError = (error) => {
  console.error('API Error:', error);
  if (error.response) {
    throw new Error(`Server Error: ${error.response.status} - ${error.response.data?.error || 'Unknown error'}`);
  }
  if (error.request) {
    throw new Error('Network Error: Please check your connection');
  }
  throw new Error(error.message || 'Unknown Error');
};

export const apiResponse = async (apiCall) => {
  try {
    return await apiCall;
  } catch (error) {
    handleAPIError(error);
    throw error;
  }
};
